import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Sun, Heart, Menu, X, UserCircle, ChevronDown } from 'lucide-react';
import { Button } from './ds/Button';
import logo from '../assets/images/blbc_logo.svg';

// Top-level nav. Items with `children` render as a dropdown on desktop
// and as an always-expanded nested list in the mobile drawer. About,
// Sermons, and Events collapsed under one "About" parent so the header
// isn't a wall of items — same destinations, one less nav slot.
const NAV = [
  { to: '/', label: 'Home' },
  {
    label: 'About',
    children: [
      { to: '/about', label: 'About Us' },
      { to: '/sermons', label: 'Sermons' },
      { to: '/events', label: 'Events' },
    ],
  },
  { to: '/ministries', label: 'Ministries' },
  { to: '/contact', label: 'Contact' },
];

function Header() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const [openDropdown, setOpenDropdown] = useState(null); // label of the open desktop dropdown
  const closeTimer = useRef(null);

  const isActiveTo = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  // A parent with children is "active" if any child destination matches.
  const isNavActive = (n) => n.children ? n.children.some((c) => isActiveTo(c.to)) : isActiveTo(n.to);

  // Small delay before closing the dropdown so a slight mouse jog off
  // the trigger toward the menu doesn't dismiss it. Cleared on re-enter.
  const openMenu = (label) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 140);
  };
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  // Close any open dropdown on route change.
  useEffect(() => { setOpenDropdown(null); }, [location.pathname]);

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Slim utility bar */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--line)', color: 'var(--text-muted)', fontSize: '13px' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '9px var(--gutter)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px', whiteSpace: 'nowrap' }}>
          <div className="ds-hide-mobile" style={{ display: 'flex', gap: '24px', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}><MapPin size={13} /> 4469 Kershaw Camden Hwy, Heath Springs, SC</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}><Phone size={13} /> (803) 273-9220</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}><Mail size={13} /> blbc@comporium.net</span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--burgundy-700)', fontWeight: 600, letterSpacing: '0.02em' }}>
              <Sun size={13} /> Sundays · 11:00 AM
            </span>
            <a
              href="https://app.brightlightbaptist.church"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                background: 'var(--burgundy-700)',
                color: '#fff',
                fontFamily: 'var(--font-ui)',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                borderRadius: 'var(--radius-pill)',
                textDecoration: 'none',
                transition: 'background 0.2s var(--ease)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--burgundy-900)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--burgundy-700)'; }}
            >
              <UserCircle size={13} /> Members
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav style={{
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'var(--white)',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: '1px solid var(--line)',
        boxShadow: scrolled ? '0 6px 24px rgba(38,12,12,0.06)' : 'none',
        transition: 'box-shadow 0.3s var(--ease), background 0.3s var(--ease)',
        position: 'relative',
      }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '18px var(--gutter)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={logo} alt="Bright Light Baptist Church" style={{ height: '100px' }} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '38px' }}>
            <ul className="ds-hide-mobile" style={{ display: 'flex', gap: '30px', listStyle: 'none', margin: 0, padding: 0, alignItems: 'center' }}>
              {NAV.map((n) => {
                const active = isNavActive(n);
                const linkStyle = {
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: 'var(--font-ui)',
                  fontSize: '15px',
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--burgundy-700)' : 'var(--text-strong)',
                  paddingBottom: '6px',
                  letterSpacing: '0.01em',
                  transition: 'color 0.2s var(--ease)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                };
                const underline = (
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    height: '2px',
                    width: active ? '100%' : '0%',
                    background: 'var(--burgundy-700)',
                    transition: 'width 0.3s var(--ease)',
                  }} />
                );

                if (n.children) {
                  const isOpen = openDropdown === n.label;
                  return (
                    <li
                      key={n.label}
                      style={{ position: 'relative' }}
                      onMouseEnter={() => openMenu(n.label)}
                      onMouseLeave={scheduleClose}
                    >
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        onClick={() => (isOpen ? setOpenDropdown(null) : openMenu(n.label))}
                        style={linkStyle}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = 'var(--burgundy-700)'; }}
                        onMouseLeaveCapture={(e) => { if (!active) e.currentTarget.style.color = 'var(--text-strong)'; }}
                      >
                        {n.label}
                        <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s var(--ease)' }} />
                        {underline}
                      </button>
                      {isOpen && (
                        <ul
                          role="menu"
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            minWidth: '200px',
                            background: 'var(--white)',
                            border: '1px solid var(--line)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: '0 14px 34px rgba(38,12,12,0.10)',
                            listStyle: 'none',
                            margin: 0,
                            padding: '8px 0',
                            zIndex: 200,
                          }}
                        >
                          {n.children.map((c) => {
                            const cActive = isActiveTo(c.to);
                            return (
                              <li key={c.to} role="none">
                                <Link
                                  to={c.to}
                                  role="menuitem"
                                  onClick={() => setOpenDropdown(null)}
                                  style={{
                                    display: 'block',
                                    padding: '10px 18px',
                                    fontFamily: 'var(--font-ui)',
                                    fontSize: '14.5px',
                                    fontWeight: cActive ? 600 : 500,
                                    color: cActive ? 'var(--burgundy-700)' : 'var(--text-strong)',
                                    textDecoration: 'none',
                                    transition: 'background 0.15s var(--ease), color 0.15s var(--ease)',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--tan-50, rgba(139,115,85,0.06))'; e.currentTarget.style.color = 'var(--burgundy-700)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = cActive ? 'var(--burgundy-700)' : 'var(--text-strong)'; }}
                                >
                                  {c.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={n.to}>
                    <Link
                      to={n.to}
                      style={linkStyle}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = 'var(--burgundy-700)'; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = 'var(--text-strong)'; }}
                    >
                      {n.label}
                      {underline}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link to="/contact" className="ds-hide-mobile">
              <Button size="sm" variant="primary" iconLeft={<Heart size={15} />}>Give</Button>
            </Link>

            {/* Hamburger — mobile only */}
            <button
              type="button"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="ds-show-mobile"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: 'var(--burgundy-700)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {open ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            className="ds-show-mobile"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'var(--white)',
              borderBottom: '1px solid var(--line)',
              boxShadow: '0 12px 30px rgba(38,12,12,0.08)',
              padding: '12px 0 20px',
            }}
          >
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {NAV.map((n) => {
                // Parents render as a non-tappable category label with
                // their children listed underneath (indented). Keeps
                // the mobile drawer flat/scannable — one tap to
                // navigate anywhere, no accordion state to manage.
                if (n.children) {
                  return (
                    <li key={n.label}>
                      <div style={{
                        padding: '14px var(--gutter) 6px',
                        fontFamily: 'var(--font-ui)',
                        fontSize: '12.5px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--text-muted)',
                      }}>
                        {n.label}
                      </div>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {n.children.map((c) => {
                          const cActive = isActiveTo(c.to);
                          return (
                            <li key={c.to}>
                              <Link
                                to={c.to}
                                style={{
                                  display: 'block',
                                  padding: '12px calc(var(--gutter) + 14px)',
                                  fontFamily: 'var(--font-ui)',
                                  fontSize: '16px',
                                  fontWeight: cActive ? 600 : 500,
                                  color: cActive ? 'var(--burgundy-700)' : 'var(--text-strong)',
                                  borderLeft: cActive ? '3px solid var(--burgundy-700)' : '3px solid transparent',
                                }}
                              >
                                {c.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                }

                const active = isActiveTo(n.to);
                return (
                  <li key={n.to}>
                    <Link
                      to={n.to}
                      style={{
                        display: 'block',
                        padding: '14px var(--gutter)',
                        fontFamily: 'var(--font-ui)',
                        fontSize: '17px',
                        fontWeight: active ? 600 : 500,
                        color: active ? 'var(--burgundy-700)' : 'var(--text-strong)',
                        borderLeft: active ? '3px solid var(--burgundy-700)' : '3px solid transparent',
                      }}
                    >
                      {n.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div style={{ padding: '14px var(--gutter) 4px', display: 'grid', gap: '10px' }}>
              <Link to="/contact" style={{ width: '100%' }}>
                <Button size="md" variant="primary" iconLeft={<Heart size={16} />} fullWidth>Give</Button>
              </Link>
              <a href="https://app.brightlightbaptist.church" target="_blank" rel="noopener noreferrer" style={{ width: '100%', textDecoration: 'none' }}>
                <Button size="md" variant="outline" iconLeft={<UserCircle size={16} />} fullWidth>Members</Button>
              </a>
            </div>
            <div style={{ padding: '20px var(--gutter) 0', display: 'grid', gap: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Phone size={14} /> (803) 273-9220</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Mail size={14} /> blbc@comporium.net</span>
              <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '8px' }}><MapPin size={14} style={{ marginTop: '2px', flex: 'none' }} /> 4469 Kershaw Camden Hwy, Heath Springs, SC 29058</span>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export default Header;
